import React, { useEffect, useState, useMemo } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { FilterOutlined } from "@ant-design/icons/es/icons";
import StarOutlined from "@ant-design/icons/es/icons/StarOutlined";
import StarFilled from "@ant-design/icons/es/icons/StarFilled";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Collapse,
  Select,
  Modal,
  DatePicker,
  Tooltip,
} from "antd";
import SearchOutlined from "@ant-design/icons/es/icons/SearchOutlined";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { isEmpty, orderBy } from "lodash";
import Tree from "../../components/Tree/Tree";
import Localize, { l, formatNumber } from "../../libs/localization";
import BannerTop from "../../components/BannerTop/BannerTop";
import Label from "../../components/Label/Label";
import Form from "../../components/Form/Form";
import InvestmentForm from "../form/InvestmentForm";
import PitchList from "../../components/List/Pitch.list";
import { getTreeCategoryAction } from "../../actions/registry/registry.category.actions";
import {
  resetSearchAction,
  searchPitchesAction,
} from "../../actions/search.action";
import { statusOptions } from "./_pitch.search.data";
import { getPitchGraphAction } from "../../actions/pitch.actions";
import { getMainCategoryAction } from "../../actions/registry/registry.main_category.actions";
import PitchGraph from "../../components/PitchGraph/PitchGraph";
import CompanyAutocomplete from "../../components/CompanyAutocomplete/CompanyAutocomplete";
import { translateRegistryOnView } from "../../libs/utils";
import TagOptions from "../../components/Tag/TagOptions";
import CreateTagModal from "../../components/CreateTagModal/CreateTagModal";
import useTags from "../../hooks/useTags";
import { TAG_ENTITY, attachTagToPitchSearchApi } from "../../api/tag.api";
import useTagAssociations from "../../hooks/useTagAssociations";
import usePermissions from "../../hooks/usePermissions";
import ErrorModal from "../../components/ErrorModal";

const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const PitchSearch = () => {
  const dispatch = useDispatch();
  const permissions = usePermissions();

  const [showOther, setShowOther] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [moveSearchButton, setMoveSearchButton] = useState(false);
  const [dataForm, setDataForm] = useState({ company_filters: {} });
  const [filters, setFilters] = useState({});
  const [companyFilters, setCompanyFilters] = useState({});
  const [companyId, setCompanyId] = useState();
  const [order, setOrder] = useState("start_date");
  const [orderDir, setOrderDir] = useState("desc");

  // TAG
  const [tagMode, setTagMode] = useState(false);
  const [tagAddModal, setAddTagModal] = useState(false);
  const [selectedPitch, setSelectedPitch] = useState([]);

  const token = useSelector((state) => state.auth.token);
  const category = useSelector((state) => state.pitch_category.data);
  const mainCategory = useSelector((state) => state.main_category.data);
  const resultSearch = useSelector((state) => state.search.results.data);
  const pitchGraph = useSelector((state) => state.pitch.graph);
  const loadingSearch = useSelector((state) => state.search.results.loading);
  const loadingGraph = useSelector((state) => state.pitch.loading);

  const {
    selectedTag,
    setSelectedTag,
    tags,
    addToTag,
    reloadTags,
    loading: loadingTags,
  } = useTags({ entity: TAG_ENTITY.PITCH });

  const pitchIds = useMemo(() => {
    return resultSearch?.pitches?.data?.map((p) => p._id) || [];
  }, [resultSearch?.pitches?.data]);

  const {
    data: tagAssociations,
    loading: tagAssociationsLoading,
    reload: reloadAssociations,
  } = useTagAssociations({ entity: TAG_ENTITY.PITCH, entityIds: pitchIds });

  useEffect(() => {
    if (category.length === 0) {
      dispatch(getTreeCategoryAction());
    }

    if (mainCategory.length === 0) {
      dispatch(getMainCategoryAction());
    }
  }, [category, mainCategory]);

  useEffect(() => {
    dispatch(resetSearchAction());
  }, []);

  const searchRequest = () => {
    const dataParams = {};
    Object.keys(dataForm).forEach((p) => {
      if (Array.isArray(dataForm[p])) {
        if (dataForm[p].length > 0) {
          dataParams[p] = dataForm[p];
        }
      } else if (dataForm[p] && dataForm[p].length > 0) {
        dataParams[p] = dataForm[p];
      } else if (dataForm[p] === true) {
        dataParams[p] = dataForm[p];
      }
      delete dataParams.name;
      delete dataParams.main_category;
    });

    if (order) {
      dataParams.sort_key = order;
      dataParams.sort_direction = orderDir;
    }

    dataParams.company_filters = {};
    if (!isEmpty(companyFilters)) {
      delete companyFilters.title;
      delete companyFilters.category;
      delete companyFilters.min_start_date;
      delete companyFilters.max_start_date;
      delete companyFilters.nielsen_filters;
      delete companyFilters.status;
      const compFilt = {};
      let company = "";
      Object.keys(companyFilters).forEach((p) => {
        if (Array.isArray(companyFilters[p])) {
          if (companyFilters[p].length > 0) {
            compFilt[p] = companyFilters[p];
          }
        } else if (companyFilters[p] && companyFilters[p].length > 0) {
          compFilt[p] = companyFilters[p];
        }
      });
      // if (Array.isArray(compFilt.name)) {
      //   company = compFilt.name.pop();
      //   compFilt.name = company;
      //   setCompanyFilters({ name: company });
      // }
      dataParams.company_filters = { ...compFilt };
      // setDataForm({ ...dataForm, company_filters: { ...compFilt } });
    }

    if (companyId) dataParams.company_filters.id = companyId;

    dataParams.pageNum = 1;
    setFilters(dataParams);
    dispatch(searchPitchesAction({ data: dataParams }));

    const dataParamsGraph = { ...dataParams };
    delete dataParamsGraph.pageNum;
    delete dataParamsGraph.limit;
    delete dataParamsGraph.sort_direction;
    delete dataParamsGraph.sort_key;
    dispatch(getPitchGraphAction({ data: dataParamsGraph }));
  };

  useEffect(() => {
    searchRequest();
  }, [orderDir, order, dataForm, companyId]);

  const associateTagPitch = async () => {
    try {
      await addToTag(selectedPitch);
      reloadAssociations();
      setTagMode(false);
      setSelectedTag("");
    } catch (error) {
      console.error("Error associating tag:", error);
      ErrorModal.show(error);
    }
  };

  const associateAll = async (e) => {
    try {
      if (selectedTag) {
        const params = { ...filters };
        delete params.pageNum;
        delete params.limit;
        delete params.sort_direction;
        delete params.sort_key;
        await attachTagToPitchSearchApi({ data: params, tagId: selectedTag });
        reloadAssociations();
        setTagMode(false);
        setSelectedTag("");
      }
    } catch (error) {
      console.error("Error associating tag to all:", error);
      ErrorModal.show(error);
    }
  };

  useEffect(() => {
    setSelectedPitch([]);
  }, [tagMode]);

  const onMoreBrandClick = (title, content) => {
    Modal.info({
      title,
      content,
      width: "800px",
      onOk: () => setShowBrandModal(false),
    });
  };

  const setStatusText = (statusKey) => {
    switch (statusKey) {
      case "ALL":
        return Localize("PAGES.PITCH.STATUS.ALL");
      case "ONGOING":
        return Localize("PAGES.PITCH.STATUS.ON_GOING");
      case "CLOSED":
        return Localize("PAGES.PITCH.STATUS.CLOSED");
      default:
        return "";
    }
  };

  const handleOrder = (val) => {
    if (val === order) {
      setOrderDir(orderDir === "asc" ? "desc" : "asc");
    } else {
      setOrderDir("asc");
    }
    setOrder(val);
  };

  const orderLabel = (value, key) => {
    return (
      <Button
        type="link"
        className={`${
          order === value ? "selected" : ""
        } searchPage__orders-button`}
        onClick={() => handleOrder(value)}
      >
        {key}
        {order === value && (
          <span className="searchPage__orders-icon">
            {orderDir == "asc" ? <UpOutlined /> : <DownOutlined />}
          </span>
        )}
      </Button>
    );
  };

  const renderSearchButton = () => (
    <Row justify="end" align="bottom" style={{ height: "100%" }}>
      <Button type="primary" onClick={() => searchRequest()}>
        <SearchOutlined />
        {Localize("COMMON.SEARCH")}
      </Button>
    </Row>
  );

  return (
    <>
      <BannerTop small>
        <h1 className="mt-4 orange-title">{Localize("PAGES.PITCH.TITLE")}</h1>
      </BannerTop>
      <div className="searchPage__filters animation fadein-up slow \">
        <Card
          bordered={false}
          style={{ borderRadius: "10px" }}
          className="companyPage__body-card companyPage__body-card--custom customCard"
        >
          <Form
            initialValues={dataForm}
            onChange={({ values, errors }) => {
              setDataForm({
                ...dataForm,
                company_filters: { ...companyFilters },
                ...values,
                ...errors,
              });
              setCompanyFilters({
                ...companyFilters,
                ...values,
                ...errors,
              });
            }}
          >
            {({ updateValue: updateValueParent }) => (
              <>
                <Row gutter={16} align="bottom">
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("PAGES.SEARCH.ORDER.TITLE")}
                    />
                    <Input
                      value={dataForm.title}
                      onChange={(e) => {
                        updateValueParent({
                          key: "title",
                          value: e.target.value,
                        });
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("PAGES.SEARCH.ORDER.CATEGORY")}
                    />
                    <Tree
                      translateEntity="Category"
                      datatree={orderBy(category, ["key"], ["asc"])}
                      onChange={(value) =>
                        updateValueParent({ key: "category", value })
                      }
                      onSelect={(value) =>
                        updateValueParent({ key: "category", value })
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("COMMON.DATE")}
                    />
                    <RangePicker
                      format="DD-MM-YYYY"
                      placeholder={[
                        Localize("COMMON.START"),
                        Localize("COMMON.END"),
                      ]}
                      className="mr-5"
                      allowEmpty={[false, true]}
                      defaultValue={[
                        dataForm.min_start_date &&
                          moment(dataForm.min_start_date),
                        dataForm.max_start_date &&
                          moment(dataForm.max_start_date),
                      ]}
                      onChange={(dates) => {
                        const [dateStart, dateEnd] = dates || [];
                        if (dateStart) {
                          updateValueParent({
                            key: "min_start_date",
                            value: dateStart.format("YYYY-MM-DD"),
                          });
                        } else {
                          updateValueParent({
                            key: "min_start_date",
                            value: "",
                          });
                        }
                        if (dateEnd) {
                          updateValueParent({
                            key: "max_start_date",
                            value: dateEnd.format("YYYY-MM-DD"),
                          });
                        } else {
                          updateValueParent({
                            key: "max_start_date",
                            value: "",
                          });
                        }
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={16} className="mt-3" align="bottom">
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("COMMON.COMPANY")}
                    />
                    <CompanyAutocomplete
                      singleValue
                      token={token}
                      onChange={(values) => {
                        setCompanyId(values[0]?.id);
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("PAGES.SEARCH.ORDER.SECTOR")}
                    />
                    <Select
                      defaultValue={dataForm.company_filters.main_category}
                      showSearch
                      allowClear
                      style={{ width: "100%" }}
                      onChange={(value) =>
                        updateValueParent({ key: "main_category", value })
                      }
                      options={
                        mainCategory.enum &&
                        orderBy(mainCategory.enum, ["id"], ["asc"]).map(
                          (e, j) => {
                            return {
                              value: e.id,
                              label: translateRegistryOnView(
                                e.id,
                                "main_category",
                                true,
                              )
                                ? translateRegistryOnView(
                                    e.id,
                                    "main_category",
                                    true,
                                  )
                                : e.id,
                              key: e.id,
                            };
                          },
                        )
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Label
                      className="filter-color"
                      value={Localize("PAGES.PITCH.STATUS_FILTER")}
                    />
                    <Select
                      style={{ width: "100%" }}
                      allowClear
                      value={setStatusText(dataForm.status)}
                      options={orderBy(statusOptions, "value")}
                      onChange={(value, option) => {
                        if (value) {
                          updateValueParent({
                            key: "status",
                            value: option.key,
                          });
                        } else {
                          updateValueParent({
                            key: "status",
                            value: "",
                          });
                        }
                      }}
                    />
                  </Col>
                </Row>
                <Row justify="space-between" className="mt-3" align="middle">
                  <Col>
                    <Button
                      type="link"
                      onClick={() => {
                        setShowOther(!showOther);
                        setMoveSearchButton(!moveSearchButton);
                      }}
                      className="secondary p-0"
                    >
                      <FilterOutlined className="mr-1" />
                      {Localize(
                        `COMMON.${!showOther ? "OTHER_FILTERS" : "CLOSE"}`,
                      )}
                    </Button>
                  </Col>
                  {/* <Col>{!moveSearchButton && renderSearchButton()}</Col> */}
                </Row>
                {showOther && (
                  <>
                    <div className="animation fadein-right slow">
                      <Form
                        initialValues={dataForm}
                        onChange={({ values, errors }) => {
                          setDataForm({
                            ...dataForm,
                            ...values,
                            ...errors,
                          });
                        }}
                      >
                        {({ updateValue }) => (
                          <Collapse ghost={true} style={{ marginLeft: -15 }}>
                            <Panel
                              key="1"
                              header={Localize(
                                "PAGES.SEARCH.ORDER.ADEVERTISING_INVESTMENTS",
                              )}
                            >
                              <InvestmentForm
                                onChange={(key, value) =>
                                  updateValue({ key, value })
                                }
                                dataForm={dataForm}
                              />
                            </Panel>
                          </Collapse>
                        )}
                      </Form>
                    </div>
                    {/* {moveSearchButton && renderSearchButton()} */}
                  </>
                )}
              </>
            )}
          </Form>
        </Card>
      </div>
      <div>
        <Row
          align="middle"
          className="searchPage__orders"
        >
          {permissions.favourites ? (
            <Tooltip title={l("TAG.ADD_TAG")}>
              <Button
                type="primary"
                shape="circle"
                onClick={() => setTagMode(!tagMode)}
                icon={
                  tagMode ? (
                    <StarFilled
                      className="mr-2 orange-title"
                      style={{ fontSize: 20, fontWeight: "normal" }}
                    />
                  ) : (
                    <StarOutlined
                      className="mr-2 orange-title"
                      style={{ fontSize: 20, fontWeight: "normal" }}
                    />
                  )
                }
              />
            </Tooltip>
          ) : null}
          <Col className="orange-title" style={{ fontWeight: "normal" }}>
            {Localize("COMMON.ORDER_BY")}
          </Col>
          <Col flex="none">
            {orderLabel("name", Localize("PAGES.SEARCH.ORDER.NAME"))}
            {orderLabel("start_date", Localize("PAGES.SEARCH.ORDER.DATE"))}
          </Col>
          <div style={{ marginLeft: "auto" }}>
            <h2
              style={{ fontWeight: "bold" }}
              className="p-0 m-0 searchPage__orders-button orange-title"
            >
              {Localize("PAGES.SEARCH.PITCH_FOUND")}{" "}
              <span className="orange-title">
                {formatNumber(
                  (resultSearch.pitches && resultSearch.pitches.totalResults) ||
                    0,
                )}
              </span>
            </h2>
          </div>
        </Row>
        <div className="animation fadein-right slow">
          <CreateTagModal
            visible={tagAddModal}
            setVisible={setAddTagModal}
            onCreate={reloadTags}
          />
          {tagMode && (
            <TagOptions
              useId
              onAddTagClick={() => setAddTagModal(true)}
              tagList={tags}
              value={selectedTag}
              onTagChange={(value) => setSelectedTag(value)}
              onAssociateClick={associateTagPitch}
              associateDisabled={!selectedTag || selectedPitch.length === 0}
              associateAllDisabled={!selectedTag}
              tooManyResultsToAssociateAll={
                !selectedTag || resultSearch?.pitches?.totalResults > 3000
              }
              onAssociateAllClick={associateAll}
            />
          )}
        </div>
      </div>
      {pitchGraph && pitchGraph.length > 0 ? (
        <PitchGraph data={pitchGraph} />
      ) : null}
      <PitchList
        pitchList={(resultSearch.pitches && resultSearch.pitches.data) || []}
        total={resultSearch.pitches && resultSearch.pitches.totalResults}
        onMoreBrandClick={onMoreBrandClick}
        loadingList={loadingSearch}
        filters={filters}
        tagMode={tagMode}
        setSelectedPitch={setSelectedPitch}
        loadingTag={tagAssociationsLoading}
        associatedTagsIds={tagAssociations}
        page={resultSearch.pitches?.currentPage}
      />
    </>
  );
};

export default PitchSearch;
